import React, { useEffect, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Button, Col, Row } from "reactstrap"
import axios from "axios"
import { api } from "../../utils/variables"
import TopAppCardM from "../MetaInsight/TopAppCardM"
import "./newRelease.css"
import TopAppCardSkeleton from "./TopAppCardSkeleton"
import FilterPanel from "../../components/KeywordTrackingNewUIPage/FilterPanel"
import { useFilters } from "../../hooks/useFilters"
import { useUrlPlatform } from "../../hooks/useUrlPlatform"
import InsightsPageHeader from "../../components/MetaInsight/InsightsPageHeader"
import SEO from "../../components/Common/SEO"
import { PAGE_SEO } from "../../config/seoConfig"
import { generateCanonicalUrl, generateItemListStructuredData, generateSlug, applySEODate } from "../../utils/seoUtils"
import PlatformsApi from "../../services/platformsApi"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"

export default function NewReleasePage() {
  const { filterSections } = useFilters()
  const { trackEvent } = useAnalytics()
  const location = useLocation()
  const navigate = useNavigate()
  // Only set when reached via a Weekly Insights "View All" click — landing here
  // directly (sidebar link, bookmark, etc.) leaves this unset, so no back button shows.
  const cameFromWeeklyInsights = location.state?.from === 'weekly-insights'
  const [newReleases, setNewReleases] = useState([])
  const [platforms, setPlatforms] = useState([])
  // Lives in the `?platform=` URL query param (defaulting to Meta) rather than
  // plain state, so it survives a round trip to an app's detail page and back,
  // and so a direct link can pre-select a platform.
  const [selectedPlatform, setSelectedPlatform] = useUrlPlatform('meta')
  const [platformsLoading, setPlatformsLoading] = useState(true)

  const [countriesList, setCountriesList] = useState([])
  const [countryOptions, setCountryOptions] = useState([])
  const [countriesLoading, setCountriesLoading] = useState(true)
  const [newReleasesCountry, setNewReleasesCountry] = useState(() => {
    try {
      return sessionStorage.getItem('newReleases_selectedCountry') || 'US'
    } catch (e) {
      return 'US'
    }
  })
  const [newReleasesLanguage, setNewReleasesLanguage] = useState(() => {
    try {
      return sessionStorage.getItem('newReleases_selectedLanguage')
    } catch (e) {
      return null
    }
  })
  const [newReleasesLastUpdated, setNewReleasesLastUpdated] = useState(null)
  const [newReleasesFilterPanelOpen, setNewReleasesFilterPanelOpen] = useState(false)
  const [newReleasesAppliedFilters, setNewReleasesAppliedFilters] = useState({})
  const [itemsToShow, setItemsToShow] = useState(8)
  const [showAll, setShowAll] = useState(false)
  const [loading, setLoading] = useState(true)
  const [selectedCardId, setSelectedCardId] = useState(null)
  const [sortedItems, setSortedItems] = useState([])
  const [isSorted, setIsSorted] = useState(false)
  const [rankSort, setRankSort] = useState(null) // null | 'asc' | 'desc'
  const isReleasedMeta = sessionStorage.getItem("releaseMeta")

  useEffect(() => {
    // load countries first, then fetch releases with country/language
    const loadCountries = async () => {
      setCountriesLoading(true)
      setPlatformsLoading(true)
      let list = []
      try {
        const [countriesRes, fetchedPlatforms] = await Promise.all([
          axios.get(`${api}/countries/`),
          PlatformsApi.fetchPlatforms()
        ])
        const res = countriesRes  // keep rest of logic using `res`
        list = res.data?.data || []
        setCountriesList(list)
        setCountryOptions(list.map(c => ({ value: c.code, label: c.name })))
        setPlatforms(fetchedPlatforms || PlatformsApi.getDefaultPlatforms())
        setPlatformsLoading(false)
        const us = list.find(x => x.code === 'US')
        const defaultLocale = us && Array.isArray(us.locales) && us.locales.length > 0 ? us.locales[0] : null
        setNewReleasesLanguage(prev => prev || defaultLocale)
      } catch (err) {
        console.error('Error loading countries:', err)
        setPlatforms(PlatformsApi.getDefaultPlatforms())
        setPlatformsLoading(false)
      } finally {
        setCountriesLoading(false)
        const initCountry = newReleasesCountry || (list && list.length > 0 ? list[0].code : 'US')
        const countryEntry = list.find(x => x.code === initCountry)
        const initLangFromList = countryEntry && Array.isArray(countryEntry.locales) && countryEntry.locales.length > 0 ? countryEntry.locales[0] : null
        const initLanguage = newReleasesLanguage || initLangFromList || null
        setNewReleasesCountry(prev => prev || initCountry)
        setNewReleasesLanguage(prev => prev || initLanguage)

        // restore saved filters (persisted across route changes)
        let savedFilters = null
        try {
          const raw = sessionStorage.getItem('newReleasesAppliedFilters')
          savedFilters = raw ? JSON.parse(raw) : null
        } catch (err) {
          savedFilters = null
        }
        setNewReleasesAppliedFilters(savedFilters || {})

        setLoading(true)
        // pass savedFilters into initial fetch so persisted filters are applied
        fetchNewReleases(initCountry, initLanguage, 1, savedFilters || {})
      }
    }

    loadCountries()
  }, [])

  const handlePlatformChange = (platformId) => {
    setSelectedPlatform(platformId)
    fetchNewReleases(newReleasesCountry, newReleasesLanguage, 1, newReleasesAppliedFilters, platformId)
  }

  // Country/language select rendering (flags, react-select styling) now lives in
  // InsightsPageHeader; this page only needs to resolve a language when the country
  // changes, so its fetch call gets the right locale.
  const handleNewReleasesCountryChange = (code, langForCountry) => {
    setNewReleasesCountry(code)
    setNewReleasesLanguage(langForCountry)
    try {
      sessionStorage.setItem('newReleases_selectedCountry', code)
      sessionStorage.setItem('newReleases_selectedLanguage', langForCountry || '')
    } catch (e) { /* ignore */ }
    setLoading(true)
    fetchNewReleases(code, langForCountry, 1, newReleasesAppliedFilters)
  }

  const handleNewReleasesLanguageChange = (val) => {
    setNewReleasesLanguage(val)
    try {
      sessionStorage.setItem('newReleases_selectedLanguage', val || '')
    } catch (e) { /* ignore */ }
    setLoading(true)
    fetchNewReleases(newReleasesCountry, val, 1, newReleasesAppliedFilters)
  }

  const fetchNewReleases = async (
    country = newReleasesCountry,
    language = newReleasesLanguage,
    page = 1,
    filters = newReleasesAppliedFilters,
    platform = selectedPlatform
  ) => {
    // If cached release meta is available and no explicit country/language provided,
    // restore from sessionStorage to avoid unnecessary fetches.
    if (isReleasedMeta && (!country && !language)) {
      setLoading(false)
      setNewReleases(JSON.parse(isReleasedMeta))
      return
    }

    try {
      // Re-arm the skeleton placeholder for every fetch (platform switch included) —
      // not just the initial mount — so the grid doesn't sit stale while new data loads.
      setLoading(true)
      const params = {}
      if (country) params.country = country
      if (language) params.language = language
      if (page) params.page = page

      // Map known filter keys to request params and serialize arrays to comma-separated lists
      if (filters && typeof filters === 'object') {
        if (filters.price) params.price = Array.isArray(filters.price) ? filters.price.join(',') : filters.price
        if (filters.rating) params.rating = Array.isArray(filters.rating) ? filters.rating.join(',') : filters.rating
        if (filters.genre) params.genre = Array.isArray(filters.genre) ? filters.genre.join(',') : filters.genre
        // some filter sets use `sort_by` while others use `sort` - support both
        if (filters.sort_by) params.sort_by = filters.sort_by
        if (filters.sort) params.sort_by = params.sort_by || filters.sort
        if (filters.bundle) params.bundle = Array.isArray(filters.bundle) ? filters.bundle.join(',') : filters.bundle
        if (filters.number_of_players) params.number_of_players = filters.number_of_players
      }

      console.info('fetchNewReleases called', { params })
      const token = localStorage.getItem("authToken")
      const headers = {}
      if (token) headers.Authorization = `Bearer ${token}`
      const res = await axios.get(`${api}/tracker/stats/${platform}/releases`, { params, headers })
      setNewReleases(res.data.games || [])
      try { sessionStorage.setItem("releaseMeta", JSON.stringify(res.data.games || [])) } catch (e) { /* ignore */ }
      setNewReleasesLastUpdated(new Date())
      trackEvent(ANALYTICS_EVENTS.FEATURE.WEEKLY_INSIGHT_VIEWED, {
        insight_type: ANALYTICS_EVENTS.WEEKLY_INSIGHT_TYPES.NEW_RELEASE,
        platform,
        country,
      })
    } catch (error) {
      console.error("Error fetching new releases:", error)
    } finally {
      setLoading(false)
    }
  }



  useEffect(() => {
    if (showAll) setItemsToShow(newReleases.length)
    else setItemsToShow(8)
  }, [showAll, newReleases.length])

  const displayApps = isSorted ? sortedItems.slice(0, itemsToShow) : newReleases.slice(0, itemsToShow)

  const parseRank = (app) => {
    if (!app) return Number.MAX_SAFE_INTEGER
    const candidates = [app.rank, app.position, app.ranking, app.rank_position, app.rankValue]
    for (let c of candidates) {
      if (c !== undefined && c !== null && c !== '' && !isNaN(Number(c))) return Number(c)
    }
    if (app.meta && (app.meta.rank !== undefined)) return Number(app.meta.rank)
    return Number.MAX_SAFE_INTEGER
  }

  const sortByRank = (order = 'asc') => {
    const arr = Array.isArray(newReleases) ? [...newReleases] : []
    arr.sort((a, b) => {
      const ra = parseRank(a)
      const rb = parseRank(b)
      return order === 'asc' ? (ra - rb) : (rb - ra)
    })
    setSortedItems(arr)
    setIsSorted(true)
  }

  const toggleRankSort = () => {
    const next = rankSort === 'asc' ? 'desc' : 'asc'
    setRankSort(next)
    sortByRank(next)
  }

  useEffect(() => {
    if (!rankSort) {
      setIsSorted(false)
      setSortedItems([])
      return
    }
    sortByRank(rankSort)
  }, [newReleases, rankSort])

  const handleCardClick = (cardId) => {
    setSelectedCardId(cardId)
  }

  const handleNewReleasesFilterApply = (filters) => {
    setNewReleasesAppliedFilters(filters)
    // persist filters so they survive route changes
    try { sessionStorage.setItem('newReleasesAppliedFilters', JSON.stringify(filters || {})) } catch (e) { /* ignore */ }
    // reset to page 1 and fetch with filters
    setLoading(true)
    fetchNewReleases(newReleasesCountry, newReleasesLanguage, 1, filters)
    setNewReleasesFilterPanelOpen(false)
  }

  const handleRemoveNewReleaseFilter = (key) => {
    const newFilters = { ...(newReleasesAppliedFilters || {}) }
    if (Object.prototype.hasOwnProperty.call(newFilters, key)) {
      delete newFilters[key]
      setNewReleasesAppliedFilters(newFilters)
      // persist updated filters and re-fetch with skeleton
      try { sessionStorage.setItem('newReleasesAppliedFilters', JSON.stringify(newFilters || {})) } catch (e) { /* ignore */ }
      setLoading(true)
      fetchNewReleases(newReleasesCountry, newReleasesLanguage, 1, newFilters)
    }
  }

  // Generate structured data for new releases
  const newReleasesStructuredData = generateItemListStructuredData(
    'New VR Game Releases 2025',
    displayApps,
    (app) => ({
      '@type': 'SoftwareApplication',
      name: app?.name || 'VR App',
      url: `${window.location.origin}/xr/app/${generateSlug(app?.name || '')}?id=${app?.item_id}`,
      applicationCategory: 'GameApplication',
      operatingSystem: 'Meta Quest',
    })
  );

  // Title/description/disclaimer reference whichever store is currently selected,
  // rather than always saying "Meta" regardless of the active platform tab.
  const platformLabel = PlatformsApi.getPlatformDisplayName(selectedPlatform, platforms)
  const platformIconUrl = PlatformsApi.getPlatformIcon(selectedPlatform, platforms)

  return (
    <React.Fragment>
      <SEO
        title={applySEODate(PAGE_SEO.newReleases.title)}
        description={PAGE_SEO.newReleases.description}
        keywords={PAGE_SEO.newReleases.keywords}
        canonical={generateCanonicalUrl('/new-releases')}
        robots={PAGE_SEO.newReleases.robots}
        structuredData={newReleasesStructuredData}
      />
      <div className="container-fluid newrelease-page" style={{ maxWidth: "100vw", padding: 32 }}>
        <div className="category_track_innersec">
          <div className="row">
            <div className="col-sm-12">
              <InsightsPageHeader
                onBack={cameFromWeeklyInsights ? () => navigate('/weekly-insights') : undefined}
                title={`Fresh on ${platformLabel}`}
                description={`Stay ahead with newly released apps of the week on the ${platformLabel} Store. Perfect for discovering emerging titles early.`}
                disclaimer={`Insights are based on ${platformLabel} Store rankings and verified release data.`}
                platforms={platforms}
                platformsLoading={platformsLoading}
                selectedPlatform={selectedPlatform}
                onPlatformChange={handlePlatformChange}
                countriesList={countriesList}
                countryOptions={countryOptions}
                countriesLoading={countriesLoading}
                selectedCountry={newReleasesCountry}
                selectedLanguage={newReleasesLanguage}
                onCountryChange={handleNewReleasesCountryChange}
                onLanguageChange={handleNewReleasesLanguageChange}
                appliedFilters={newReleasesAppliedFilters}
                onRemoveFilter={handleRemoveNewReleaseFilter}
                lastUpdated={newReleasesLastUpdated}
                onOpenFilters={() => setNewReleasesFilterPanelOpen(true)}
                rankSort={rankSort}
                onToggleSort={toggleRankSort}
              />
            </div>
          </div>

          {/* Content Area */}
          <div className="newrelease-content">
            {/* App Grid */}
            {loading ? (
              <Row className="g-3">
                {Array.from({ length: 4 }).map((_, index) => (
                  <Col key={index} xs={12} sm={6}>
                    <div className="newrelease-card">
                      <TopAppCardSkeleton />
                    </div>
                  </Col>
                ))}
              </Row>
            ) : (
              <>
                {displayApps.length > 0 ? (
                  <Row className="g-3">
                    {displayApps.map((app, index) => (
                      <Col key={app?.item_id || index} xs={12} sm={6}>
                        <div
                          className={`newrelease-card ${selectedCardId === app?.item_id ? 'selected' : ''}`}
                          onClick={() => handleCardClick(app?.item_id)}
                        >
                          <TopAppCardM
                            gamedata={app}
                            priceLayout="discounts"
                            showEnds={false}
                            headingLevel="h2"
                            insightType={ANALYTICS_EVENTS.WEEKLY_INSIGHT_TYPES.NEW_RELEASE}
                            sourceSurface="new_release_page"
                            platformIcon={platformIconUrl}
                            platformName={platformLabel}
                          />
                        </div>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <p style={{ color: '#A1ACB8', fontSize: 16 }}>
                      {Object.keys(newReleasesAppliedFilters || {}).length > 0
                        ? 'No results matched with filter.'
                        : 'No results to display.'}
                    </p>
                  </div>
                )}

                {/* See More/Less Button */}
                {!loading && (
                  <div className="text-center mt-4">
                    {!showAll && newReleases.length > 4 ? (
                      <Button
                        className="see-more-btn"
                        onClick={() => setShowAll(true)}
                      >
                        See More
                      </Button>
                    ) : showAll ? (
                      <Button
                        className="see-less-btn"
                        onClick={() => setShowAll(false)}
                      >
                        See Less
                      </Button>
                    ) : null}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <FilterPanel
        open={newReleasesFilterPanelOpen}
        sections={filterSections.length > 0 ? filterSections : undefined}
        // Platform is already selectable via the Meta Quest/Steam tabs above -
        // don't duplicate it inside the filter panel.
        hidePlatformSection
        onClose={() => setNewReleasesFilterPanelOpen(false)}
        onApply={handleNewReleasesFilterApply}
        initialValues={newReleasesAppliedFilters}
      />
    </React.Fragment>
  )
}